/**
 * Minimal replacement for react-router v5 `matchPath`, covering only the
 * pattern features used in this app: `:param` and optional `:param?` segments.
 * Kept v5-compatible (prefix matching by default, case-insensitive, trailing
 * slash tolerated) because `state.routeParams.route` stores v5-style patterns
 * that are matched both against pathnames and against other patterns.
 */

export type MatchPathOptions = {
  path: string;
  exact?: boolean;
};

export type PathMatch = {
  path: string;
  url: string;
  isExact: boolean;
  /** Keyed by the `:param` names in `path`, so only known at runtime. */
  params: Record<string, string | undefined>;
};

type CompiledPath = {
  regexp: RegExp;
  keys: string[];
};

const compileCache = new Map<string, CompiledPath>();

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function compilePath(path: string, exact: boolean): CompiledPath {
  const cacheKey = `${exact ? 'e' : 'p'}:${path}`;
  const cached = compileCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const keys: string[] = [];
  let source = '';
  for (const segment of path.split('/')) {
    if (segment === '') {
      continue;
    }
    const paramMatch = /^:(\w+)(\?)?$/.exec(segment);
    if (paramMatch) {
      keys.push(paramMatch[1]);
      source += paramMatch[2] ? '(?:/([^/]+))?' : '/([^/]+)';
    } else {
      source += `/${escapeRegExp(segment)}`;
    }
  }

  const regexp = exact
    ? new RegExp(`^${source}/?$`, 'i')
    : new RegExp(`^${source}(?=/|$)`, 'i');
  const compiled = { regexp, keys };
  compileCache.set(cacheKey, compiled);
  return compiled;
}

export function matchPath(
  pathname: string,
  options: string | MatchPathOptions,
): PathMatch | null {
  const { path, exact = false } =
    typeof options === 'string' ? { path: options } : options;

  const { regexp, keys } = compilePath(path, exact);
  const match = regexp.exec(pathname);
  if (!match) {
    return null;
  }

  const url = match[0] === '' ? '/' : match[0];
  const params = Object.fromEntries(
    keys.map((key, index) => [key, match[index + 1]]),
  );

  return {
    path,
    url,
    isExact: pathname === url,
    params,
  };
}
